import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, Loader2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { ParticleOrb } from "./ParticleOrb";

const PERKS = [
  "Resumen y advertencias de cada paciente",
  "Nuevo paciente, receta, referido, cita y certificado con IA",
  "Lectura de cédula y documentos",
  "Respuestas clínicas con el contexto de tu consulta",
];

export const AiLocked = ({ billing }) => {
  const [busy, setBusy] = useState(false);
  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No pude iniciar el pago");
      setBusy(false);
    }
  };
  const trial = billing?.trial_available;
  const lapsed = ["canceled", "past_due", "unpaid", "incomplete_expired"].includes(billing?.status);

  return (
    <div className="flex-1 min-h-0 overflow-y-auto cai-scroll px-5 pb-5 cai-fade-in" data-testid="cai-locked">
      <ParticleOrb size={190} />
      <div className="text-center text-white">
        <div className="text-[18px] font-semibold">Activa ConsuDoctor AI</div>
        <div className="text-[12.5px] text-white/85 mt-1">
          {lapsed ? "Tu suscripción no está activa. Reactívala para seguir usando la IA." : "Tu asistente clínico profesional, listo cuando lo necesites."}
        </div>
      </div>
      <div className="cai-glass rounded-[22px] p-4 mt-4 space-y-2">
        {PERKS.map((p) => (
          <div key={p} className="flex items-start gap-2 text-[12.5px] text-white">
            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {p}
          </div>
        ))}
      </div>
      <div className="text-center text-white mt-4">
        <span className="text-[28px] font-semibold" data-testid="cai-price">US${billing?.price ?? 19.99}</span>
        <span className="text-[13px] text-white/80"> / mes</span>
        {trial && <div className="text-[12px] text-white/90 mt-0.5">{billing?.trial_days ?? 7} días gratis · cancela cuando quieras</div>}
      </div>
      <button
        type="button"
        onClick={activate}
        disabled={busy}
        data-testid="cai-activate"
        className="mt-4 w-full h-12 rounded-full bg-white text-[#5B8FD0] text-[14px] font-semibold flex items-center justify-center gap-2 hover:bg-white/90 active:scale-[0.98] transition-[background-color,transform] disabled:opacity-60"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {trial ? "Empezar prueba gratis" : "Activar ahora"}
      </button>
      <div className="text-center text-[10.5px] text-white/75 mt-2.5">Pago seguro con Stripe. Se renueva cada mes.</div>
    </div>
  );
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }) : "");

export const PlanCard = ({ billing, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (!billing) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(billing.cancel_at_period_end ? "/ai/billing/resume" : "/ai/billing/cancel");
      onChange(data);
      toast.success(data.cancel_at_period_end ? "Suscripción cancelada al final del periodo" : "Suscripción reactivada");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  let text = "IA de por vida · sin cobros";
  if (!billing.courtesy) {
    if (billing.cancel_at_period_end) text = `Se desactiva el ${fmt(billing.current_period_end)}`;
    else if (billing.status === "trialing") text = `Prueba gratis hasta el ${fmt(billing.trial_end)}`;
    else text = `Activa · se renueva el ${fmt(billing.current_period_end)}`;
  }
  return (
    <div className="mx-3 mb-3 p-3 rounded-[18px] bg-[#1E3A5F]/15 border border-white/20 flex items-center gap-3 text-white" data-testid="cai-plan">
      <span className="w-9 h-9 rounded-full bg-white/20 border border-white/30 grid place-items-center shrink-0">
        <Sparkles className="w-4 h-4" strokeWidth={1.7} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[12.5px] font-semibold">ConsuDoctor AI · US${billing.price}/mes</span>
        <span className="block text-[11px] text-white/80 truncate" data-testid="cai-plan-status">{text}</span>
      </span>
      {!billing.courtesy && billing.status && (
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          data-testid="cai-plan-toggle"
          className="h-8 px-3 rounded-full bg-white/20 border border-white/30 text-[11px] font-semibold hover:bg-white/30 transition-colors disabled:opacity-50"
        >
          {billing.cancel_at_period_end ? "Reactivar" : "Cancelar"}
        </button>
      )}
    </div>
  );
};
